import { z } from 'zod';
import { BusinessCodeSchema, RecordIdSchema } from './identifiers';
import { LocalizedLabelSchema } from './localized';
import { DecimalStringSchema, MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Accounting foundation — `FIN-COA`, `FIN-GL`, `FIN-ARAP`, `FIN-REPORT` (BMP-2 package 1, ADR-0034).
 *
 * **Mechanism, not policy.** `SD-08` (an appointed accounting reviewer) is open, so nothing here names
 * an account, a posting map or a tax rate. The product enforces the rules of double entry, which are
 * not a client's choice: debit equals credit exactly, a posted entry is never edited, a correction is a
 * reversing entry, nothing posts to a closed period. Which accounts exist and which business event posts
 * where is configuration an authorized person enters (`BD-37`, `BD-18`).
 *
 * Money is a decimal string in one currency per entry — the legal entity's (`BD-54`). Amounts are
 * positive; a line's side says debit or credit.
 */

/* --------------------------------------------------------------- chart of accounts */

export const ACCOUNT_CATEGORIES = ['asset', 'liability', 'equity', 'revenue', 'expense'] as const;
export const AccountCategorySchema = z.enum(ACCOUNT_CATEGORIES);
export type AccountCategory = z.infer<typeof AccountCategorySchema>;

export const ENTRY_SIDES = ['debit', 'credit'] as const;
export const EntrySideSchema = z.enum(ENTRY_SIDES);
export type EntrySide = z.infer<typeof EntrySideSchema>;

/** The side on which an account's balance normally sits — fixed by its category, not configurable. */
export function normalBalanceOf(category: AccountCategory): EntrySide {
  return category === 'asset' || category === 'expense' ? 'debit' : 'credit';
}

/**
 * Which party a line on the account must name. A subledger account cannot be posted without its party,
 * which is what lets a customer or vendor statement be read straight from the ledger (FIN-ARAP).
 */
export const SUBLEDGER_KINDS = ['none', 'customer', 'vendor', 'employee'] as const;
export const SubledgerKindSchema = z.enum(SUBLEDGER_KINDS);
export type SubledgerKind = z.infer<typeof SubledgerKindSchema>;

export const PARTY_KINDS = ['customer', 'vendor', 'employee'] as const;
export const PartyKindSchema = z.enum(PARTY_KINDS);
export type PartyKind = z.infer<typeof PartyKindSchema>;

export const LEDGER_ACCOUNT_STATES = ['active', 'inactive'] as const;
export const LedgerAccountStateSchema = z.enum(LEDGER_ACCOUNT_STATES);

/** An account code as the client's chart writes it: `1100`, `4-01-002`, `2.1.3`. */
export const AccountCodeSchema = z
  .string()
  .trim()
  .regex(/^[0-9A-Z][0-9A-Z.-]{0,23}$/, { message: 'ACCOUNT_CODE_EXPECTED' });

export const LedgerAccountSchema = z.strictObject({
  accountId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  code: AccountCodeSchema,
  name: LocalizedLabelSchema,
  category: AccountCategorySchema,
  normalBalance: EntrySideSchema,
  parentAccountId: RecordIdSchema.optional(),
  /** Only a postable (leaf) account takes ledger lines; a heading account only sums its children. */
  postable: z.boolean(),
  subledger: SubledgerKindSchema,
  requiresCostCenter: z.boolean(),
  /**
   * Where the account appears on the financial statements (FIN-REPORT-009). Presentation only: it may
   * change after postings without touching a ledger line, and must fit the category. Absent: the
   * account is shown as unclassified and the statements say so.
   */
  statementLine: z.string().min(1).max(40).optional(),
  state: LedgerAccountStateSchema,
  /** True once any line has posted to it; category, subledger and postability are then fixed. */
  hasPostings: z.boolean(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type LedgerAccount = z.infer<typeof LedgerAccountSchema>;

export const CreateLedgerAccountSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  code: AccountCodeSchema,
  name: LocalizedLabelSchema,
  category: AccountCategorySchema,
  parentAccountId: RecordIdSchema.optional(),
  postable: z.boolean().default(true),
  subledger: SubledgerKindSchema.default('none'),
  requiresCostCenter: z.boolean().default(false),
  statementLine: z.string().min(1).max(40).optional(),
});
export type CreateLedgerAccount = z.infer<typeof CreateLedgerAccountSchema>;

/**
 * An account edit. The code, parent, category, postability and subledger are fixed once anything has
 * posted to the account (`ACCOUNT_HAS_POSTINGS`): changing them would silently reclassify lines
 * already in the ledger. `parentAccountId: null` moves the account to the top level.
 */
export const UpdateLedgerAccountSchema = z
  .strictObject({
    code: AccountCodeSchema.optional(),
    name: LocalizedLabelSchema.optional(),
    category: AccountCategorySchema.optional(),
    parentAccountId: RecordIdSchema.nullable().optional(),
    postable: z.boolean().optional(),
    subledger: SubledgerKindSchema.optional(),
    requiresCostCenter: z.boolean().optional(),
    /** `null` removes the classification. */
    statementLine: z.string().min(1).max(40).nullable().optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine(
    (value) =>
      value.statementLine !== undefined ||
      value.code !== undefined ||
      value.name !== undefined ||
      value.category !== undefined ||
      value.parentAccountId !== undefined ||
      value.postable !== undefined ||
      value.subledger !== undefined ||
      value.requiresCostCenter !== undefined,
    { message: 'NOTHING_TO_UPDATE' },
  );
export type UpdateLedgerAccount = z.infer<typeof UpdateLedgerAccountSchema>;

export const LedgerAccountLifecycleSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type LedgerAccountLifecycle = z.infer<typeof LedgerAccountLifecycleSchema>;

export const LedgerAccountQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  category: AccountCategorySchema.optional(),
  state: LedgerAccountStateSchema.optional(),
  postable: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
  search: z.string().trim().min(1).max(60).optional(),
});
export type LedgerAccountQuery = z.infer<typeof LedgerAccountQuerySchema>;

export const LedgerAccountListSchema = z.strictObject({ items: z.array(LedgerAccountSchema) });

/**
 * Where an account is used, so a person can see what a change or a deactivation would affect before
 * making it. Counts and configuration references only — no amounts of other people's records.
 */
export const LedgerAccountUsageSchema = z.strictObject({
  accountId: RecordIdSchema,
  hasPostings: z.boolean(),
  postedLines: z.number().int().nonnegative(),
  children: z.strictObject({
    active: z.number().int().nonnegative(),
    inactive: z.number().int().nonnegative(),
  }),
  /** Posting rules naming the account as a fixed account; an active one blocks deactivation. */
  postingRules: z.array(
    z.strictObject({
      ruleId: RecordIdSchema,
      event: z.string(),
      version: z.number().int().positive(),
      state: z.string(),
    }),
  ),
  /** Treasury accounts (bank, cashbox) whose ledger account this is. */
  treasuryAccounts: z.array(
    z.strictObject({
      accountId: RecordIdSchema,
      code: z.string(),
      name: LocalizedLabelSchema,
      kind: z.string(),
      state: z.string(),
    }),
  ),
  /** Draft or pending journal entries that name the account; they will be refused while it is inactive. */
  openDrafts: z.number().int().nonnegative(),
});
export type LedgerAccountUsage = z.infer<typeof LedgerAccountUsageSchema>;

/** A legal entity as accounting shows it: whose books, in which currency. */
export const FinanceEntitySchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  code: z.string(),
  name: LocalizedLabelSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
});
export type FinanceEntity = z.infer<typeof FinanceEntitySchema>;
export const FinanceEntityListSchema = z.strictObject({ items: z.array(FinanceEntitySchema) });

/* -------------------------------------------------------- fiscal years and periods */

export const FISCAL_YEAR_STATES = ['open', 'closed'] as const;
export const FiscalYearStateSchema = z.enum(FISCAL_YEAR_STATES);

/**
 * A period moves forward only, except a closed one that may be reopened through approval. A locked
 * period is final: nothing posts to it and nothing reopens it (FIN-GL-002).
 */
export const PERIOD_STATES = ['future', 'open', 'closed', 'locked'] as const;
export const PeriodStateSchema = z.enum(PERIOD_STATES);
export type PeriodState = z.infer<typeof PeriodStateSchema>;

export const PERIOD_ACTIONS = ['open', 'close', 'lock', 'reopen'] as const;
export const PeriodActionSchema = z.enum(PERIOD_ACTIONS);
export type PeriodAction = z.infer<typeof PeriodActionSchema>;

const PERIOD_TRANSITIONS: Record<PeriodAction, { from: PeriodState; to: PeriodState }> = {
  open: { from: 'future', to: 'open' },
  close: { from: 'open', to: 'closed' },
  lock: { from: 'closed', to: 'locked' },
  reopen: { from: 'closed', to: 'open' },
};

/** The state an action moves a period to, or `undefined` when the action is not allowed from `from`. */
export function periodTransition(from: PeriodState, action: PeriodAction): PeriodState | undefined {
  const rule = PERIOD_TRANSITIONS[action];
  return rule.from === from ? rule.to : undefined;
}

export const PeriodSchema = z.strictObject({
  periodId: RecordIdSchema,
  fiscalYearId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  sequence: z.number().int().min(1).max(12),
  code: z.string(),
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema,
  state: PeriodStateSchema,
  /** An open or reopen waiting on approval (G-04). */
  pendingAction: z.enum(['open', 'reopen']).optional(),
  approvalRequestId: z.string().optional(),
  openedAt: InstantSchema.optional(),
  closedAt: InstantSchema.optional(),
  lockedAt: InstantSchema.optional(),
  version: z.number().int().positive(),
});
export type Period = z.infer<typeof PeriodSchema>;

export const FiscalYearSchema = z.strictObject({
  fiscalYearId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  code: BusinessCodeSchema,
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema,
  state: FiscalYearStateSchema,
  /** The year-end closing entry, once generated. */
  closingEntryId: RecordIdSchema.optional(),
  periods: z.array(PeriodSchema),
  createdAt: InstantSchema,
});
export type FiscalYear = z.infer<typeof FiscalYearSchema>;

export const CreateFiscalYearSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  code: BusinessCodeSchema,
  /** The first day of a month — the configured fiscal-year start month (`BD-17`). */
  startsOn: BusinessDateSchema,
});
export type CreateFiscalYear = z.infer<typeof CreateFiscalYearSchema>;

export const PeriodTransitionSchema = z.strictObject({
  action: PeriodActionSchema,
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type PeriodTransition = z.infer<typeof PeriodTransitionSchema>;

export const FiscalYearListSchema = z.strictObject({ items: z.array(FiscalYearSchema) });

export const YearEndCloseSchema = z.strictObject({
  /** The equity account revenue and expense balances are closed into (`BD-37`). */
  retainedEarningsAccountId: RecordIdSchema,
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type YearEndClose = z.infer<typeof YearEndCloseSchema>;

/* ------------------------------------------------------------------- journal entries */

export const JOURNALS = [
  'general',
  'opening',
  'adjustment',
  'closing',
  'sales',
  'receivables',
  'payables',
  'cash',
  'bank',
  'payroll',
  'assets',
  'inventory',
  'construction',
] as const;
export const JournalSchema = z.enum(JOURNALS);
export type Journal = z.infer<typeof JournalSchema>;

/** Journals a person may write to by hand; the rest are written only by posting rules. */
export const MANUAL_JOURNALS = ['general', 'opening', 'adjustment'] as const;

export const ENTRY_KINDS = ['manual', 'opening', 'system', 'reversal', 'closing'] as const;
export const EntryKindSchema = z.enum(ENTRY_KINDS);
export type EntryKind = z.infer<typeof EntryKindSchema>;

export const ENTRY_STATES = [
  'draft',
  'pendingApproval',
  'posted',
  'reversed',
  'discarded',
] as const;
export const EntryStateSchema = z.enum(ENTRY_STATES);
export type EntryState = z.infer<typeof EntryStateSchema>;

/** Accounting dimensions carried by every line (FIN-COA-002). References are validated on posting. */
export const DimensionsSchema = z.strictObject({
  branchId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  departmentId: RecordIdSchema.optional(),
  costCenterCode: BusinessCodeSchema.optional(),
});
export type Dimensions = z.infer<typeof DimensionsSchema>;

export const PartyRefSchema = z.strictObject({
  kind: PartyKindSchema,
  id: RecordIdSchema,
});
export type PartyRef = z.infer<typeof PartyRefSchema>;

/** A positive amount: an entry line is never zero and never negative; its side says which way. */
export const PositiveAmountSchema = DecimalStringSchema.refine(
  (value) => !value.startsWith('-') && !/^0(\.0+)?$/.test(value),
  { message: 'POSITIVE_AMOUNT_EXPECTED' },
);

export const JournalLineInputSchema = z.strictObject({
  accountId: RecordIdSchema,
  side: EntrySideSchema,
  amount: PositiveAmountSchema,
  description: z.string().trim().max(240).optional(),
  party: PartyRefSchema.optional(),
  dimensions: DimensionsSchema.optional(),
});
export type JournalLineInput = z.infer<typeof JournalLineInputSchema>;

export const JournalLineSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  accountId: RecordIdSchema,
  accountCode: AccountCodeSchema,
  accountName: LocalizedLabelSchema,
  side: EntrySideSchema,
  amount: MoneySchema,
  description: z.string().optional(),
  party: PartyRefSchema.optional(),
  dimensions: DimensionsSchema,
});
export type JournalLine = z.infer<typeof JournalLineSchema>;

export const SourceRefSchema = z.strictObject({
  type: z.string().regex(/^[a-z][A-Za-z]{1,39}$/, { message: 'SOURCE_TYPE_EXPECTED' }),
  id: z.string().min(1).max(80),
  /** The human reference printed on the source — a receipt number, an invoice number. */
  reference: z.string().max(80).optional(),
});
export type SourceRef = z.infer<typeof SourceRefSchema>;

export const JournalEntrySchema = z.strictObject({
  entryId: RecordIdSchema,
  entryNumber: z.string().optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  journal: JournalSchema,
  kind: EntryKindSchema,
  entryDate: BusinessDateSchema,
  periodId: RecordIdSchema.optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  memo: z.string(),
  source: SourceRefSchema.optional(),
  /** The posting rule a system entry was built from, with its version. */
  postingRule: z.strictObject({ ruleId: RecordIdSchema, version: z.number().int() }).optional(),
  lines: z.array(JournalLineSchema),
  totalDebit: MoneySchema,
  totalCredit: MoneySchema,
  balanced: z.boolean(),
  state: EntryStateSchema,
  reversesEntryId: RecordIdSchema.optional(),
  reversedByEntryId: RecordIdSchema.optional(),
  reversalReason: z.string().optional(),
  approvalRequestId: z.string().optional(),
  /** An opening entry posted with lines repeating accounts an earlier opening entry carries. */
  repeatedOpeningAcknowledged: z.boolean().optional(),
  createdByAccountId: z.string(),
  postedByAccountId: z.string().optional(),
  postedAt: InstantSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type JournalEntry = z.infer<typeof JournalEntrySchema>;

const IdempotencyKeySchema = z.string().trim().min(8).max(100);

export const CreateJournalEntrySchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema.optional(),
  journal: z.enum(MANUAL_JOURNALS).default('general'),
  entryDate: BusinessDateSchema,
  memo: z.string().trim().min(3).max(500),
  lines: z.array(JournalLineInputSchema).min(2).max(200),
  idempotencyKey: IdempotencyKeySchema,
});
export type CreateJournalEntry = z.infer<typeof CreateJournalEntrySchema>;

export const UpdateJournalEntrySchema = z.strictObject({
  entryDate: BusinessDateSchema.optional(),
  memo: z.string().trim().min(3).max(500).optional(),
  lines: z.array(JournalLineInputSchema).min(2).max(200).optional(),
  expectedVersion: z.number().int().positive(),
});
export type UpdateJournalEntry = z.infer<typeof UpdateJournalEntrySchema>;

export const PostJournalEntrySchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  /**
   * For an opening entry only: the person confirms that lines on an account (and party) another
   * posted opening entry already carries are **additional** balances — a second batch, with the
   * opening-equity offset repeated — and not the same balance entered twice. Recorded on the entry.
   */
  acknowledgeRepeatedOpening: z.literal(true).optional(),
});

export const DiscardJournalEntrySchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const ReverseJournalEntrySchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  /** The date the reversal posts on; it must fall in an open period. */
  reversalDate: BusinessDateSchema,
  idempotencyKey: IdempotencyKeySchema,
});
export type ReverseJournalEntry = z.infer<typeof ReverseJournalEntrySchema>;

export const JournalEntryQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  state: EntryStateSchema.optional(),
  journal: JournalSchema.optional(),
  kind: EntryKindSchema.optional(),
  accountId: RecordIdSchema.optional(),
  sourceType: SourceRefSchema.shape.type.optional(),
  sourceId: z.string().min(1).max(80).optional(),
  from: BusinessDateSchema.optional(),
  to: BusinessDateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type JournalEntryQuery = z.infer<typeof JournalEntryQuerySchema>;

export const JournalEntryPageSchema = z.strictObject({
  items: z.array(JournalEntrySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type JournalEntryPage = z.infer<typeof JournalEntryPageSchema>;

/* ---------------------------------------------------------------- opening balances */

/**
 * How a treasury account's opening and the ledger opening entries compare on its chart account:
 *
 * - `noOpening` — neither side has recorded one yet;
 * - `notLinked` — the treasury account has no chart account, so nothing can reach the ledger;
 * - `postedByRule` — the treasury opening posts through the `treasury.opening` rule (or is queued to);
 * - `matched` / `differs` — the opening entry carries it: equal, or not, to the treasury book opening;
 * - `ledgerMissing` — the treasury opening says the entry carries it, but no posted entry does yet;
 * - `treasuryMissing` — the entry carries a balance on the account but no treasury opening exists.
 */
export const OPENING_LINK_STATUSES = [
  'noOpening',
  'notLinked',
  'postedByRule',
  'matched',
  'differs',
  'ledgerMissing',
  'treasuryMissing',
] as const;
export type OpeningLinkStatus = (typeof OPENING_LINK_STATUSES)[number];

export const OpeningBalanceWorkspaceSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Opening entries (journal `opening`) in every state; posted ones are final, corrected by reversal. */
  entries: z.array(
    z.strictObject({
      entryId: RecordIdSchema,
      entryNumber: z.string().optional(),
      entryDate: BusinessDateSchema,
      memo: z.string(),
      state: EntryStateSchema,
      total: DecimalStringSchema,
      lines: z.number().int().nonnegative(),
      postedAt: InstantSchema.optional(),
      reversedByEntryId: RecordIdSchema.optional(),
    }),
  ),
  /** Per account, the net of the posted (unreversed) opening entries. */
  accounts: z.array(
    z.strictObject({
      accountId: RecordIdSchema,
      code: AccountCodeSchema,
      name: LocalizedLabelSchema,
      category: AccountCategorySchema,
      debit: DecimalStringSchema,
      credit: DecimalStringSchema,
    }),
  ),
  totals: z.strictObject({
    debit: DecimalStringSchema,
    credit: DecimalStringSchema,
    balanced: z.boolean(),
  }),
  treasury: z.array(
    z.strictObject({
      treasuryAccountId: RecordIdSchema,
      code: z.string(),
      name: LocalizedLabelSchema,
      kind: z.string(),
      ledgerAccount: z
        .strictObject({ accountId: RecordIdSchema, code: AccountCodeSchema })
        .optional(),
      bookOpening: DecimalStringSchema.optional(),
      bookOpeningDate: BusinessDateSchema.optional(),
      treatment: z.enum(['postingRule', 'openingEntry']).optional(),
      /** Net debit the posted opening entries carry on the linked chart account. */
      ledgerOpening: DecimalStringSchema,
      /** For a rule-posted opening: whether its event has posted or still waits. */
      postingEventState: z.enum(['pending', 'posted', 'cancelled']).optional(),
      status: z.enum(OPENING_LINK_STATUSES),
    }),
  ),
});
export type OpeningBalanceWorkspace = z.infer<typeof OpeningBalanceWorkspaceSchema>;

/* ------------------------------------------------- historical onboarding (BD-63) */

/**
 * How pre-cutover history reaches the ledger. Release 1 supports **one** treatment, the proposed
 * BD-63 (b): imported historical contracts and receipts post nothing and move no treasury balance;
 * a separate, explicit opening entry carries the starting balances. Posting every historical event is
 * not offered — it would need the full posting map and would duplicate the opening entry.
 */
export const ONBOARDING_ACCOUNTING_TREATMENTS = ['openingBalances'] as const;
/** BD-63 (c): historical records keep their own `HCT`/`HRC` series and the client's original numbers. */
export const ONBOARDING_NUMBERING_MODES = ['separateHistoricalSeries'] as const;

export const ONBOARDING_STATES = ['cutoverMissing', 'notAdopted', 'adopted', 'superseded'] as const;
export type OnboardingState = (typeof ONBOARDING_STATES)[number];

const OnboardingRecordSchema = z.strictObject({
  recordId: RecordIdSchema,
  kind: z.enum(['adoption', 'correction']),
  cutoverDate: BusinessDateSchema,
  /** The version of `finance.onboardingCutoverDate` this record was made against. */
  settingVersion: z.number().int().positive(),
  accountingTreatment: z.enum(ONBOARDING_ACCOUNTING_TREATMENTS),
  numbering: z.enum(ONBOARDING_NUMBERING_MODES),
  previousCutoverDate: BusinessDateSchema.optional(),
  reason: z.string(),
  actorAccountId: z.string(),
  recordedAt: InstantSchema,
});
export type OnboardingRecord = z.infer<typeof OnboardingRecordSchema>;

export const OnboardingStatusSchema = z.strictObject({
  state: z.enum(ONBOARDING_STATES),
  cutoverDate: BusinessDateSchema.nullable(),
  /** The current version of the cutover setting — sent back as `expectedSettingVersion`. */
  settingVersion: z.number().int().nonnegative(),
  accountingTreatment: z.enum(ONBOARDING_ACCOUNTING_TREATMENTS),
  numbering: z.enum(ONBOARDING_NUMBERING_MODES),
  /** The adoption in force, when the state is `adopted`. */
  adoption: OnboardingRecordSchema.optional(),
  history: z.array(OnboardingRecordSchema),
  historicalRecords: z.strictObject({
    contracts: z.number().int().nonnegative(),
    receipts: z.number().int().nonnegative(),
    latestContractDate: BusinessDateSchema.optional(),
    latestReceiptDate: BusinessDateSchema.optional(),
  }),
  /** True once historical records exist: the cutover changes only through a controlled correction. */
  cutoverLocked: z.boolean(),
  decision: z.literal('BD-63'),
});
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;

export const AdoptOnboardingSchema = z.strictObject({
  cutoverDate: BusinessDateSchema,
  /** `settingVersion` from the status the person read; 0 when the date was never set. */
  expectedSettingVersion: z.number().int().nonnegative(),
  accountingTreatment: z.enum(ONBOARDING_ACCOUNTING_TREATMENTS),
  numbering: z.enum(ONBOARDING_NUMBERING_MODES),
  /** The person confirms they read the plain-language explanation of the treatment. */
  acknowledged: z.literal(true),
  reason: z.string().trim().min(3).max(500),
});
export type AdoptOnboarding = z.infer<typeof AdoptOnboardingSchema>;

/**
 * Move the cutover date after historical records exist. Refused unless every historical contract and
 * receipt still falls before the new date and no receipt recorded through the normal workflow does.
 * The adoption must then be confirmed again.
 */
export const CorrectCutoverSchema = z.strictObject({
  cutoverDate: BusinessDateSchema,
  expectedSettingVersion: z.number().int().positive(),
  reason: z.string().trim().min(10).max(500),
});
export type CorrectCutover = z.infer<typeof CorrectCutoverSchema>;

/* ---------------------------------------------------------------- posting rules */

/**
 * The business events that can post, and what each one supplies (FIN-GL-006).
 *
 * `amounts` are the money figures a rule line may take; `accounts` are ledger accounts the event itself
 * knows — the bank account a receipt was deposited to, the cashbox a payment came from. A rule picks a
 * fixed account or one of these. The catalog is product vocabulary; which account each line posts to is
 * the client's configuration (`BD-18`).
 */
export const POSTING_EVENTS = {
  'receipt.recorded': { amounts: ['amount'], accounts: ['settlement'], party: 'customer' },
  'customerCredit.applied': { amounts: ['amount'], accounts: [], party: 'customer' },
  'installment.waived': { amounts: ['amount'], accounts: [], party: 'customer' },
  'lateFee.charged': { amounts: ['amount'], accounts: [], party: 'customer' },
  'changeFee.charged': { amounts: ['amount'], accounts: [], party: 'customer' },
  'refund.paid': { amounts: ['amount'], accounts: ['settlement'], party: 'customer' },
  'contract.activated': { amounts: ['contractValue'], accounts: [], party: 'customer' },
  'contract.cancelled': { amounts: ['penalty', 'refundable'], accounts: [], party: 'customer' },
  'invoice.issued': { amounts: ['net', 'tax', 'gross'], accounts: [], party: 'customer' },
  'creditNote.issued': { amounts: ['net', 'tax', 'gross'], accounts: [], party: 'customer' },
  'debitNote.issued': { amounts: ['net', 'tax', 'gross'], accounts: [], party: 'customer' },
  'bank.deposit': { amounts: ['amount'], accounts: ['bank', 'counter'], party: null },
  'bank.withdrawal': { amounts: ['amount'], accounts: ['bank', 'counter'], party: null },
  'bank.charge': { amounts: ['amount'], accounts: ['bank'], party: null },
  'bank.interest': { amounts: ['amount'], accounts: ['bank'], party: null },
  'treasury.transfer': { amounts: ['amount'], accounts: ['from', 'to'], party: null },
  // BMP-2 package 3: a treasury account's opening balance, cash received and paid at a cash desk, and an
  // approved outgoing payment whose execution was evidenced. `counter` is the chart account on the
  // other side when the person knew it; without it a rule must name a fixed account (ADR-0036).
  'treasury.opening': { amounts: ['amount'], accounts: ['account'], party: null },
  'cash.received': { amounts: ['amount'], accounts: ['cash', 'counter'], party: null },
  'cash.paid': { amounts: ['amount'], accounts: ['cash', 'counter'], party: null },
  'payment.executed': { amounts: ['amount'], accounts: ['settlement', 'counter'], party: null },
  // `retention` is the part of the gross held back under the order's terms (BD-51); a rule may credit
  // it to a retention-payable account instead of the vendor.
  'vendorInvoice.recorded': {
    amounts: ['net', 'tax', 'gross', 'retention'],
    accounts: [],
    party: 'vendor',
  },
  'vendorAdvance.applied': { amounts: ['amount'], accounts: [], party: 'vendor' },
  'retention.released': { amounts: ['amount'], accounts: [], party: 'vendor' },
  'vendorPayment.paid': {
    amounts: ['amount', 'retention'],
    accounts: ['settlement'],
    party: 'vendor',
  },
  'goods.received': { amounts: ['amount'], accounts: [], party: 'vendor' },
  // Accepted goods sent back to the supplier, at the receipt's cost (2026-10-17).
  'goods.returned': { amounts: ['amount'], accounts: [], party: 'vendor' },
  // The supplier's credit note against a recorded invoice (2026-10-17).
  'vendorCreditNote.recorded': { amounts: ['net', 'tax', 'gross'], accounts: [], party: 'vendor' },
  'stock.issued': { amounts: ['amount'], accounts: [], party: null },
  'stock.adjusted': { amounts: ['gain', 'loss'], accounts: [], party: null },
  'construction.certified': {
    amounts: ['gross', 'retention', 'advanceRecovery', 'deductions', 'net'],
    accounts: [],
    party: 'vendor',
  },
  // `expense` is the ledger account the person chose on the expense (FIN-CASH-004); the credit side is
  // the rule's (an accrued-expenses or employee-payable account).
  'expense.approved': { amounts: ['amount'], accounts: ['expense'], party: null },
  /** Approved spend out of a cash advance: the expense, against what the holder was advanced. */
  'cashAdvance.spent': { amounts: ['amount'], accounts: ['expense'], party: null },
  /**
   * A cash advance paid out by treasury (2026-10-17): an amount the holder owes back or accounts
   * for — an employee-advance asset, never an expense. `settlement` is the paying account's chart
   * account; the rule names the advances account.
   */
  'cashAdvance.paid': { amounts: ['amount'], accounts: ['settlement'], party: null },
  /** Unused advance cash returned into a cashbox or bank account: reduces what the holder owes. */
  'cashAdvance.returned': { amounts: ['amount'], accounts: ['cash'], party: null },
  'asset.acquired': { amounts: ['cost'], accounts: ['settlement'], party: null },
  'asset.depreciated': { amounts: ['amount'], accounts: [], party: null },
  'asset.disposed': {
    amounts: ['cost', 'accumulated', 'proceeds', 'gain', 'loss'],
    accounts: ['settlement'],
    party: null,
  },
  'payroll.posted': {
    amounts: ['gross', 'deductions', 'net', 'employerCost'],
    accounts: [],
    party: null,
  },
  'advance.paid': { amounts: ['amount'], accounts: ['settlement'], party: 'employee' },
  // Revenue recognition under the adopted rule (FIN-REVREC, BD-71): a rule typically moves the amount
  // from a contract liability (or receivable) to revenue; derecognition is the opposite movement.
  'revenue.recognized': { amounts: ['amount'], accounts: [], party: 'customer' },
  'revenue.derecognized': { amounts: ['amount'], accounts: [], party: 'customer' },
} as const satisfies Record<
  string,
  { amounts: readonly string[]; accounts: readonly string[]; party: PartyKind | null }
>;
export type PostingEvent = keyof typeof POSTING_EVENTS;
export const POSTING_EVENT_KEYS = Object.keys(POSTING_EVENTS) as PostingEvent[];
export const PostingEventSchema = z.enum(POSTING_EVENT_KEYS as [PostingEvent, ...PostingEvent[]]);

/**
 * Events the Release-1 workflows actually report today (collections, invoicing, treasury). The rest
 * of the catalog is defined ahead for later packages (payables, stock, construction, assets, payroll);
 * a missing mapping for one of those blocks nothing yet.
 */
export const POSTING_EVENTS_IN_USE: readonly PostingEvent[] = [
  'receipt.recorded',
  'customerCredit.applied',
  'installment.waived',
  'lateFee.charged',
  'changeFee.charged',
  'refund.paid',
  'contract.cancelled',
  'invoice.issued',
  'creditNote.issued',
  'debitNote.issued',
  'bank.deposit',
  'bank.withdrawal',
  'bank.charge',
  'bank.interest',
  'treasury.transfer',
  'treasury.opening',
  'cash.received',
  'cash.paid',
  'payment.executed',
  // Procurement and payables (BMP-2 package 4) report these since 2026-10-15.
  'vendorInvoice.recorded',
  'goods.received',
  'goods.returned',
  'vendorCreditNote.recorded',
  'vendorAdvance.applied',
  'retention.released',
  // Construction (package 4b) and fixed assets (package 5).
  'construction.certified',
  'asset.acquired',
  'asset.depreciated',
  'asset.disposed',
  // Payroll (package 7).
  'payroll.posted',
  // Expenses (FIN-CASH-004).
  'expense.approved',
  'cashAdvance.spent',
  'cashAdvance.paid',
  'cashAdvance.returned',
  // Revenue recognition (FIN-REVREC, 2026-10-17).
  'revenue.recognized',
  'revenue.derecognized',
];

/**
 * Clearly fictional sample figures for a rule preview — internally consistent where an event's
 * amounts are related (gross = net + tax), so a correct rule previews balanced. A preview writes
 * nothing; the person may replace any figure.
 */
export function samplePostingAmounts(event: PostingEvent): Record<string, string> {
  switch (event) {
    case 'invoice.issued':
    case 'creditNote.issued':
    case 'debitNote.issued':
    case 'vendorInvoice.recorded':
    case 'vendorCreditNote.recorded':
      return { net: '1000.00', tax: '140.00', gross: '1140.00' };
    case 'contract.cancelled':
      return { penalty: '100.00', refundable: '900.00' };
    case 'vendorPayment.paid':
      return { amount: '950.00', retention: '50.00' };
    case 'stock.adjusted':
      return { gain: '100.00', loss: '40.00' };
    case 'construction.certified':
      return {
        gross: '1000.00',
        retention: '50.00',
        advanceRecovery: '100.00',
        deductions: '50.00',
        net: '800.00',
      };
    case 'asset.disposed':
      return {
        cost: '1000.00',
        accumulated: '600.00',
        proceeds: '500.00',
        gain: '100.00',
        loss: '0.00',
      };
    case 'payroll.posted':
      return { gross: '1000.00', deductions: '200.00', net: '800.00', employerCost: '150.00' };
    default:
      return Object.fromEntries(POSTING_EVENTS[event].amounts.map((key) => [key, '1000.00']));
  }
}

/**
 * `draft`: saved, editable, posts nothing. `pendingApproval`: activation waiting on
 * `finance.postingRule.activate` approval; it posts nothing until approved.
 */
export const POSTING_RULE_STATES = [
  'draft',
  'pendingApproval',
  'active',
  'retired',
  'rejected',
] as const;

export const PostingRuleLineSchema = z
  .strictObject({
    side: EntrySideSchema,
    /** `fixed` posts to `accountId`; `context` to the account the event supplies under `contextKey`. */
    accountSource: z.enum(['fixed', 'context']),
    accountId: RecordIdSchema.optional(),
    contextKey: z.string().min(1).max(40).optional(),
    amountKey: z.string().min(1).max(40),
    /** Carry the event's party (customer, vendor, employee) onto the line — required on a subledger account. */
    withParty: z.boolean().default(false),
  })
  .refine(
    (line) =>
      line.accountSource === 'fixed'
        ? line.accountId !== undefined && line.contextKey === undefined
        : line.contextKey !== undefined && line.accountId === undefined,
    { message: 'ACCOUNT_SOURCE_MISMATCH' },
  );
export type PostingRuleLine = z.infer<typeof PostingRuleLineSchema>;

export const PostingRuleSchema = z.strictObject({
  ruleId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  event: PostingEventSchema,
  description: z.string(),
  journal: JournalSchema,
  lines: z.array(PostingRuleLineSchema),
  state: z.enum(POSTING_RULE_STATES),
  approvalRequestId: z.string().optional(),
  version: z.number().int().positive(),
  /** The decision the configuration still waits on; a configured rule is not an approved policy. */
  decision: z.literal('BD-18'),
  createdByAccountId: z.string(),
  createdAt: InstantSchema,
  /** Edit counter of a draft (`expectedRevision`); 1 for a rule stored before it existed. */
  revision: z.number().int().positive(),
  activatedAt: InstantSchema.optional(),
  retiredAt: InstantSchema.optional(),
});
export type PostingRule = z.infer<typeof PostingRuleSchema>;

export const CreatePostingRuleSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  event: PostingEventSchema,
  description: z.string().trim().min(3).max(300),
  journal: JournalSchema,
  lines: z.array(PostingRuleLineSchema).min(2).max(12),
  /** Save without activating: the rule posts nothing until it is activated. */
  asDraft: z.boolean().optional(),
});
export type CreatePostingRule = z.infer<typeof CreatePostingRuleSchema>;

/** A draft edit; omitted fields stay as they are. */
export const UpdatePostingRuleDraftSchema = z.strictObject({
  description: z.string().trim().min(3).max(300).optional(),
  journal: JournalSchema.optional(),
  lines: z.array(PostingRuleLineSchema).min(2).max(12).optional(),
  expectedRevision: z.number().int().positive(),
});
export type UpdatePostingRuleDraft = z.infer<typeof UpdatePostingRuleDraftSchema>;

export const ActivatePostingRuleSchema = z.strictObject({
  expectedRevision: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});

/** Preview a rule against sample figures. Structured input only — no formula, no expression. */
export const PostingRulePreviewRequestSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  event: PostingEventSchema,
  lines: z.array(PostingRuleLineSchema).min(1).max(12),
  /** Sample amounts per amount key; missing keys take `samplePostingAmounts`. */
  amounts: z.record(z.string().min(1).max(40), DecimalStringSchema).optional(),
});
export type PostingRulePreviewRequest = z.infer<typeof PostingRulePreviewRequestSchema>;

export const PostingRulePreviewSchema = z.strictObject({
  currency: z.string().regex(/^[A-Z]{3}$/),
  amounts: z.record(z.string(), DecimalStringSchema),
  lines: z.array(
    z.strictObject({
      side: EntrySideSchema,
      amountKey: z.string(),
      amount: DecimalStringSchema,
      /** A fixed account, or absent when the event supplies it (`contextKey`). */
      account: z
        .strictObject({
          accountId: RecordIdSchema,
          code: AccountCodeSchema,
          name: LocalizedLabelSchema,
        })
        .optional(),
      contextKey: z.string().optional(),
      withParty: z.boolean(),
    }),
  ),
  totalDebit: DecimalStringSchema,
  totalCredit: DecimalStringSchema,
  balanced: z.boolean(),
  /** Every problem with the configuration, by line (-1 = the rule as a whole); empty when it would post. */
  issues: z.array(z.strictObject({ index: z.number().int(), code: z.string() })),
});
export type PostingRulePreview = z.infer<typeof PostingRulePreviewSchema>;

/** Per event: whether a mapping exists, and what is waiting on it. */
export const PostingCoverageSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  events: z.array(
    z.strictObject({
      event: PostingEventSchema,
      inUse: z.boolean(),
      active: z.strictObject({ ruleId: RecordIdSchema, version: z.number().int() }).optional(),
      pendingApproval: z
        .strictObject({ ruleId: RecordIdSchema, version: z.number().int() })
        .optional(),
      draft: z.strictObject({ ruleId: RecordIdSchema, version: z.number().int() }).optional(),
      queued: z.number().int().nonnegative(),
      queuedByReason: z.record(z.string(), z.number().int().nonnegative()),
      posted: z.number().int().nonnegative(),
    }),
  ),
});
export type PostingCoverage = z.infer<typeof PostingCoverageSchema>;

export const PostingRuleListSchema = z.strictObject({ items: z.array(PostingRuleSchema) });

export const POSTING_EVENT_STATES = ['pending', 'posted', 'cancelled'] as const;
export const PostingEventStateSchema = z.enum(POSTING_EVENT_STATES);

/** Why an event has not posted yet. Each names what a person must do. */
export const UNPOSTED_REASONS = [
  'ruleNotConfigured',
  'periodNotOpen',
  'accountInvalid',
  'unbalanced',
  'contextAccountMissing',
] as const;
export const UnpostedReasonSchema = z.enum(UNPOSTED_REASONS);
export type UnpostedReason = z.infer<typeof UnpostedReasonSchema>;

export const PostingEventRecordSchema = z.strictObject({
  eventId: RecordIdSchema,
  event: PostingEventSchema,
  legalEntityId: RecordIdSchema,
  source: SourceRefSchema,
  eventDate: BusinessDateSchema,
  amounts: z.record(z.string(), MoneySchema),
  party: PartyRefSchema.optional(),
  dimensions: DimensionsSchema,
  state: PostingEventStateSchema,
  reason: UnpostedReasonSchema.optional(),
  entryId: RecordIdSchema.optional(),
  attempts: z.number().int().nonnegative(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type PostingEventRecord = z.infer<typeof PostingEventRecordSchema>;

export const PostingEventQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  state: PostingEventStateSchema.optional(),
  reason: UnpostedReasonSchema.optional(),
  event: PostingEventSchema.optional(),
  sourceType: SourceRefSchema.shape.type.optional(),
  sourceId: z.string().min(1).max(80).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type PostingEventQuery = z.infer<typeof PostingEventQuerySchema>;

export const PostingEventPageSchema = z.strictObject({
  items: z.array(PostingEventRecordSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});

/** One event retried by a person; a concurrent retry or the sweep can never post it twice. */
export const RETRY_OUTCOMES = ['posted', 'stillPending', 'alreadyPosted', 'cancelled'] as const;
export const RetryPostingEventResultSchema = z.strictObject({
  outcome: z.enum(RETRY_OUTCOMES),
  event: PostingEventRecordSchema,
});
export type RetryPostingEventResult = z.infer<typeof RetryPostingEventResultSchema>;

export const ProcessPostingEventsResultSchema = z.strictObject({
  posted: z.number().int().nonnegative(),
  stillPending: z.number().int().nonnegative(),
});

/* ---------------------------------------------------------------------- reports */

const ReportFiltersShape = {
  legalEntityId: RecordIdSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  branchId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  departmentId: RecordIdSchema.optional(),
  costCenterCode: BusinessCodeSchema.optional(),
};

export const TrialBalanceQuerySchema = z
  .strictObject(ReportFiltersShape)
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type TrialBalanceQuery = z.infer<typeof TrialBalanceQuerySchema>;

/** Facts every report states about itself, so a reader knows what it covers (FIN-REPORT-001). */
export const ReportMetaSchema = z.strictObject({
  generatedAt: InstantSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  /**
   * True when the reader's data scope hides some lines: totals are then the reader's slice, not the
   * entity's, and may not balance. Hidden lines are never counted in a total (no leak through sums).
   */
  scopeLimited: z.boolean(),
});
export type ReportMeta = z.infer<typeof ReportMetaSchema>;

export const TrialBalanceRowSchema = z.strictObject({
  accountId: RecordIdSchema,
  code: AccountCodeSchema,
  name: LocalizedLabelSchema,
  category: AccountCategorySchema,
  opening: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
  movement: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
  closing: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
});
export type TrialBalanceRow = z.infer<typeof TrialBalanceRowSchema>;

export const TrialBalanceSchema = z.strictObject({
  meta: ReportMetaSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  rows: z.array(TrialBalanceRowSchema),
  totals: z.strictObject({
    opening: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
    movement: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
    closing: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
  }),
  balanced: z.boolean(),
});
export type TrialBalance = z.infer<typeof TrialBalanceSchema>;

export const LedgerQuerySchema = z
  .strictObject({
    ...ReportFiltersShape,
    accountId: RecordIdSchema,
    limit: z.coerce.number().int().min(1).max(500).default(200),
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type LedgerQuery = z.infer<typeof LedgerQuerySchema>;

export const LedgerLineSchema = z.strictObject({
  entryId: RecordIdSchema,
  entryNumber: z.string().optional(),
  entryDate: BusinessDateSchema,
  journal: JournalSchema,
  memo: z.string(),
  description: z.string().optional(),
  source: SourceRefSchema.optional(),
  party: PartyRefSchema.optional(),
  debit: DecimalStringSchema,
  credit: DecimalStringSchema,
  /** Running balance on the account's normal side. */
  balance: DecimalStringSchema,
});
export type LedgerLine = z.infer<typeof LedgerLineSchema>;

export const GeneralLedgerSchema = z.strictObject({
  meta: ReportMetaSchema,
  account: z.strictObject({
    accountId: RecordIdSchema,
    code: AccountCodeSchema,
    name: LocalizedLabelSchema,
    category: AccountCategorySchema,
    normalBalance: EntrySideSchema,
  }),
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  opening: DecimalStringSchema,
  lines: z.array(LedgerLineSchema),
  /** More lines exist in the range than were returned; totals and closing still cover all of them. */
  truncated: z.boolean(),
  totals: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
  closing: DecimalStringSchema,
});
export type GeneralLedger = z.infer<typeof GeneralLedgerSchema>;

export const PartyStatementQuerySchema = z
  .strictObject({
    legalEntityId: RecordIdSchema,
    partyKind: PartyKindSchema,
    partyId: RecordIdSchema,
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    limit: z.coerce.number().int().min(1).max(500).default(200),
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type PartyStatementQuery = z.infer<typeof PartyStatementQuerySchema>;

export const PartyStatementSchema = z.strictObject({
  meta: ReportMetaSchema,
  party: PartyRefSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  /** Positive means the party owes the company (a customer) or the company owes the party (a vendor). */
  opening: DecimalStringSchema,
  lines: z.array(
    z.strictObject({
      ...LedgerLineSchema.shape,
      accountCode: AccountCodeSchema,
    }),
  ),
  truncated: z.boolean(),
  totals: z.strictObject({ debit: DecimalStringSchema, credit: DecimalStringSchema }),
  closing: DecimalStringSchema,
});
export type PartyStatement = z.infer<typeof PartyStatementSchema>;

/* ---------------------------------------------------------------- vocabulary */

/** Operations accounting submits to the approval engine; governed only where a policy names them. */
export const FINANCE_APPROVAL_OPERATIONS = {
  periodOpen: 'finance.period.open',
  periodReopen: 'finance.period.reopen',
  journalPost: 'finance.journal.post',
  postingRuleActivate: 'finance.postingRule.activate',
} as const;

export const FINANCE_AUDIT_ACTIONS = {
  accountCreated: 'finance.account.created',
  accountUpdated: 'finance.account.updated',
  accountDeactivated: 'finance.account.deactivated',
  accountReactivated: 'finance.account.reactivated',
  fiscalYearCreated: 'finance.fiscalYear.created',
  fiscalYearClosed: 'finance.fiscalYear.closed',
  periodTransitioned: 'finance.period.transitioned',
  periodTransitionRequested: 'finance.period.transitionRequested',
  periodTransitionRefused: 'finance.period.transitionRefused',
  entryDrafted: 'finance.entry.drafted',
  entryUpdated: 'finance.entry.updated',
  entryDiscarded: 'finance.entry.discarded',
  entrySubmitted: 'finance.entry.submitted',
  entryPosted: 'finance.entry.posted',
  entryReversed: 'finance.entry.reversed',
  entryRefused: 'finance.entry.refused',
  postingRuleCreated: 'finance.postingRule.created',
  postingRuleUpdated: 'finance.postingRule.updated',
  postingRuleSubmitted: 'finance.postingRule.submitted',
  postingRuleRetired: 'finance.postingRule.retired',
  postingRuleActivated: 'finance.postingRule.activated',
  postingEventRecorded: 'finance.postingEvent.recorded',
  postingEventsProcessed: 'finance.postingEvent.processed',
  postingEventRetried: 'finance.postingEvent.retried',
  onboardingAdopted: 'finance.onboarding.adopted',
  onboardingCutoverCorrected: 'finance.onboarding.cutoverCorrected',
  revenueRecognized: 'finance.revenue.recognized',
  recognitionReversed: 'finance.revenue.recognitionReversed',
  statementExported: 'finance.statement.exported',
} as const;
